/**
 * Keeping the room token alive for as long as the meeting is.
 *
 * Phoenix signs room tokens for an hour, and nothing renewed them. The meeting
 * itself carried on — the socket is only checked when it connects — but an
 * hour in, every request made with the token answered 401: a document or video
 * shared fifteen minutes after the scheduled end failed with "Upload failed
 * (401)", shared files stopped loading, and a socket that dropped could not
 * reconnect at all.
 *
 * Now the backend re-issues the token while the meeting is live
 * (`POST /api/v1/rooms/:roomId/token`), and this module:
 *  - renews a few minutes before expiry (`scheduleRoomTokenRenewal`),
 *  - hands the freshest token to anything that asks (`currentRoomToken`), so
 *    the credentials object — and the socket keyed on it — never changes,
 *  - retries a room-token request once after a 401 (`roomFetch`, or
 *    `withRoomToken` for an API client that throws instead), and
 *  - turns "the meeting has ended" into `MeetingEndedError`, which callers show
 *    as a sentence instead of a status code.
 */
import { config } from '@/config/env'
import { uploadErrorMessage } from '@/lib/upload-policy'
import { canReachNetwork } from '@/clientWeb/components/ui/network-failure'
import type { SkipNetworkErrorInit } from '@/clientWeb/components/ui/network-error-dialog'

const API = config.apiUrl

/** Renew this long before expiry. */
const RENEW_BEFORE_MS = 5 * 60_000
/** And never sooner than this after scheduling, whatever the clock says. */
const MIN_DELAY_MS = 30_000

export class MeetingEndedError extends Error {
  constructor() {
    super('This meeting has ended, so files can no longer be shared or opened here.')
    this.name = 'MeetingEndedError'
  }
}

/** The freshest token known per room. */
const latest = new Map<string, string>()
const inFlight = new Map<string, Promise<string>>()

/** `exp` of a JWT in ms, or null if it cannot be read. */
export function tokenExpiryMs(token: string): number | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const exp = (JSON.parse(atob(b64)) as { exp?: unknown }).exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * Was this room token issued to a guest?
 *
 * The guest routes mint `guest_<uuid>` identities and never `is_host`. A guest
 * holding anything else is holding someone else's token — a signed-in
 * account's left behind in this tab — and joining on it makes them that
 * account in the room, host included.
 */
export function isGuestRoomToken(token: string): boolean {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(b64)) as { sub?: unknown; is_host?: unknown }
    return typeof claims.sub === 'string' && claims.sub.startsWith('guest_') && claims.is_host !== true
  } catch {
    return false
  }
}

/** The token to use for `roomId`: a renewed one if there is one, else `fallback`. */
export function currentRoomToken(roomId: string, fallback: string): string {
  const renewed = latest.get(roomId)
  if (!renewed) return fallback
  // A newer token from a fresh join wins over an older renewal.
  return (tokenExpiryMs(fallback) ?? 0) > (tokenExpiryMs(renewed) ?? 0) ? fallback : renewed
}

/** How long a 429 asks us to wait (`Retry-After` / `RateLimit-Reset`, seconds), capped. */
function retryDelayMs(res: Response): number {
  const secs = Number(res.headers.get('Retry-After') ?? res.headers.get('RateLimit-Reset'))
  return Number.isFinite(secs) && secs > 0 ? Math.min(secs, 10) * 1000 : 2000
}

/** Ask the backend for a fresh token. One request per room at a time. */
export function renewRoomToken(roomId: string, token: string): Promise<string> {
  const pending = inFlight.get(roomId)
  if (pending) return pending
  const request = (async () => {
    const ask = () =>
      fetch(`${API}/api/v1/rooms/${encodeURIComponent(roomId)}/token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentRoomToken(roomId, token)}` },
      })
    let res = await ask()
    // Rate limited: wait as long as the server says, within reason, and ask
    // once more. Giving up here is what turns a busy minute into "rejoin the
    // meeting" on an upload whose token simply needed renewing.
    if (res.status === 429) {
      await new Promise((r) => setTimeout(r, retryDelayMs(res)))
      res = await ask()
    }
    if (res.status === 410) throw new MeetingEndedError()
    if (!res.ok) throw new Error(`room token renewal failed (${res.status})`)
    const body = (await res.json()) as { roomToken?: string }
    if (!body.roomToken) throw new Error('room token renewal returned no token')
    latest.set(roomId, body.roomToken)
    return body.roomToken
  })()
  inFlight.set(roomId, request)
  request.then(
    () => inFlight.delete(roomId),
    () => inFlight.delete(roomId),
  )
  return request
}

/**
 * A token good for at least another minute: the current one, or a renewed one.
 * Never throws — on failure it returns what it had, and the server decides.
 */
export async function ensureFreshRoomToken(roomId: string, token: string): Promise<string> {
  const current = currentRoomToken(roomId, token)
  const exp = tokenExpiryMs(current)
  if (exp === null || exp - Date.now() > 60_000) return current
  try {
    return await renewRoomToken(roomId, token)
  } catch {
    return current
  }
}

/** Sends one request. `fetch`, or a stand-in for what fetch cannot do. */
export type FetchTransport = (url: string, init: RequestInit) => Promise<Response>

/**
 * `fetch` with the room token: the freshest one, renewed once on a 401.
 * Throws `MeetingEndedError` when the meeting is over.
 *
 * `transport` replaces `fetch` for the request itself: an upload that reports
 * its progress sends through XMLHttpRequest, since fetch cannot say how much of
 * a body has gone.
 */
export async function roomFetch(
  roomId: string,
  token: string,
  url: string,
  init: RequestInit = {},
  transport: FetchTransport = fetch,
): Promise<Response> {
  const send = (t: string) =>
    transport(url, { ...init, headers: { ...(init.headers ?? {}), Authorization: `Bearer ${t}` } })
  const first = await send(await ensureFreshRoomToken(roomId, token))
  if (first.status !== 401) return first
  let renewed: string
  try {
    renewed = await renewRoomToken(roomId, token)
  } catch (err) {
    if (err instanceof MeetingEndedError) throw err
    return first
  }
  return send(renewed)
}

/**
 * `roomFetch` for a request made through an API client that throws on a
 * refusal — `meetingsApi`, whose `ApiError` carries the `status` — rather than
 * handing back the response: `call` gets the freshest token, and a renewed one
 * once if that is refused 401. A renewal that fails throws.
 */
export async function withRoomToken<T>(
  roomId: string,
  token: string,
  call: (token: string) => Promise<T>,
): Promise<T> {
  try {
    return await call(await ensureFreshRoomToken(roomId, token))
  } catch (err) {
    if ((err as { status?: unknown } | null)?.status !== 401) throw err
  }
  return call(await renewRoomToken(roomId, token))
}

/**
 * Keep `roomId`'s token renewed until the returned function is called.
 * A meeting that has ended stops the loop; a failed renewal tries again in a
 * minute.
 *
 * `onEnded` hears the first: the Screen Share page ends itself on it, since a
 * page whose room channel is down has no other way to learn the meeting is over.
 */
export function scheduleRoomTokenRenewal(
  roomId: string,
  token: string,
  opts: { onEnded?: () => void } = {},
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  let stopped = false
  const plan = (delayMs: number) => {
    timer = setTimeout(() => {
      void renewRoomToken(roomId, token).then(
        (fresh) => {
          if (stopped) return
          const exp = tokenExpiryMs(fresh)
          plan(exp ? exp - Date.now() - RENEW_BEFORE_MS : 50 * 60_000)
        },
        (err) => {
          if (stopped) return
          if (err instanceof MeetingEndedError) {
            opts.onEnded?.()
            return
          }
          plan(60_000)
        },
      )
    }, Math.max(MIN_DELAY_MS, delayMs))
  }
  const exp = tokenExpiryMs(currentRoomToken(roomId, token))
  plan(exp ? exp - Date.now() - RENEW_BEFORE_MS : 50 * 60_000)
  return () => {
    stopped = true
    if (timer) clearTimeout(timer)
  }
}

/**
 * What a failed upload says. A bare status code ("Upload failed (401)") told
 * the user nothing; these say what happened and what to do.
 */
export function uploadFailureMessage(status: number): string {
  if (status === 401 || status === 403) return 'Your access to this meeting has expired. Rejoin the meeting to share files.'
  // A 413 is not always ours. The app takes 25 MB, but a proxy in front of it
  // can refuse far less — nginx's default is 1 MB — and "larger than 25 MB"
  // on a 2 MB file sent people looking for a problem with their file.
  if (status === 413) return 'The server would not accept a file this large. Try a smaller file, or ask your administrator to raise the upload limit.'
  if (status === 429) return 'Too many uploads at once. Wait a moment and try again.'
  if (status === 415) return 'That type of file cannot be shared.'
  return `The file could not be uploaded (${status}). Please try again.`
}

/**
 * The message for a failed upload response. The server names the exact reason
 * when it refused the file itself (type, contents, name, size); that wording
 * wins over the status-code fallback, which can only guess.
 */
export async function uploadFailureMessageFor(res: Response): Promise<string> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // No JSON body (a proxy's HTML error page): the status decides.
  }
  return uploadErrorMessage(body) ?? uploadFailureMessage(res.status)
}

/** What a failed upload says when this device has no connection. */
export const UPLOAD_OFFLINE_MESSAGE =
  "Upload failed: you're offline. Check your internet connection and try again."

/**
 * What a failed upload says when the network works but no answer came back.
 * Not size: anything over the 25 MB limit is refused before it is sent.
 */
export const UPLOAD_UNREACHABLE_MESSAGE = 'The file could not be uploaded. Please try again.'

/**
 * What a failed upload says when there was no response to read.
 *
 * It used to be one sentence for two causes, "it may be too large for the
 * server, or the connection dropped", so a presenter whose Wi-Fi went was told
 * to try a smaller file. Offline is the usual cause, so it is asked about:
 * `navigator.onLine` is trusted when it says offline, and otherwise one tiny
 * request decides, since online only means some interface is up.
 */
export async function uploadUnreachableMessage(): Promise<string> {
  const online = typeof navigator === 'undefined' || navigator.onLine !== false
  const reachable = await canReachNetwork(online, () =>
    fetch(`${API || window.location.origin}/?_netcheck=${Date.now()}`, {
      method: 'HEAD',
      // Any answer proves the network; no-cors keeps a cross-origin API from
      // failing the probe on CORS alone.
      mode: 'no-cors',
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
      skipNetworkError: true,
    } as SkipNetworkErrorInit),
  )
  return reachable ? UPLOAD_UNREACHABLE_MESSAGE : UPLOAD_OFFLINE_MESSAGE
}
